import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);
  const relatedArticles = articles
    .filter((a) => a.id !== id && article && a.category === article.category)
    .slice(0, 3);

  if (!article) {
    return (
      <>
        <SEOHead title="文章未找到" description="抱歉，您访问的文章不存在。" />
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">文章未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors duration-200">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categorySlug = categories.find((c) => c.name === article.category)?.slug || '';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.image,
    url: `https://luckilypink.com.cn/article/${article.id}`,
    publisher: {
      '@type': 'Organization',
      name: '文爱社区',
    },
    articleBody: article.content.replace(/<[^>]*>/g, ''),
    keywords: article.tags.join(', '),
  };

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={`${article.title},${article.category},文爱社区,文字情感社区,文爱交流平台`}
        canonicalUrl={`https://luckilypink.com.cn/article/${article.id}`}
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogType="article"
        articleData={{
          publishedTime: article.date,
          author: article.author,
          tags: article.tags,
        }}
        jsonLd={jsonLd}
      />
      <Navbar />
      <main className="min-h-screen pt-20 pb-16">
        <article className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.category, href: `/category/${categorySlug}` },
                { label: article.title },
              ]}
            />
          </div>

          <header className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium whitespace-nowrap">
                {article.category}
              </span>
              <span className="text-xs text-foreground-400">{article.readTime} 分钟阅读</span>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4 leading-snug">
              {article.title}
            </h1>
            <div className="flex items-center gap-3">
              <img
                src={article.authorAvatar}
                alt={article.author}
                className="w-10 h-10 rounded-full object-cover"
              />
              <div>
                <p className="text-sm font-medium text-foreground-800">{article.author}</p>
                <p className="text-xs text-foreground-400">{article.date} 发布</p>
              </div>
            </div>
          </header>

          <div className="mb-8 rounded-lg overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-auto object-cover"
              title={`${article.title} - 文爱社区文章配图`}
            />
          </div>

          <div
            className="prose-custom text-foreground-700 leading-relaxed text-sm md:text-base mb-10"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="flex flex-wrap gap-2 mb-10">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap"
              >
                #{tag}
              </span>
            ))}
          </div>

          <div className="flex items-center justify-between py-6 border-t border-b border-background-200 mb-10">
            <div className="flex items-center gap-4">
              <button className="flex items-center gap-1.5 text-foreground-500 hover:text-primary-500 transition-colors duration-200 cursor-pointer">
                <i className="ri-heart-line w-5 h-5 flex items-center justify-center"></i>
                <span className="text-sm">{article.likes}</span>
              </button>
              <button className="flex items-center gap-1.5 text-foreground-500 hover:text-primary-500 transition-colors duration-200 cursor-pointer">
                <i className="ri-chat-1-line w-5 h-5 flex items-center justify-center"></i>
                <span className="text-sm">{article.comments}</span>
              </button>
            </div>
            <button className="flex items-center gap-1.5 text-foreground-500 hover:text-primary-500 transition-colors duration-200 cursor-pointer">
              <i className="ri-share-forward-line w-5 h-5 flex items-center justify-center"></i>
              <span className="text-sm">分享</span>
            </button>
          </div>

          {relatedArticles.length > 0 && (
            <section aria-labelledby="related-heading">
              <h2 id="related-heading" className="font-heading text-lg font-semibold text-foreground-900 mb-5">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group bg-background-100 rounded-lg overflow-hidden cursor-pointer"
                  >
                    <div className="h-32 overflow-hidden">
                      <img
                        src={rel.image}
                        alt={rel.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-3">
                      <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                        {rel.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />

      <style>{`
        .prose-custom p {
          margin-bottom: 1.25rem;
          line-height: 1.8;
        }
      `}</style>
    </>
  );
}